import React, { useEffect, useState } from "react";
import DataTable from "components/basic/data-table/DataTable";
import { TabPanel, TabView } from "primereact/tabview";
import "./ScheduledTaskHistoryTable.scss";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import Editor from "@monaco-editor/react";
import { useGetAirflowTaskHistories } from "api/api-hooks/airflow-hooks";
import { ScheduledTaskDTO } from "api/model/airflow-task/ScheduledTaskDTO";
import { IScheduledTaskHistoryDTO } from "api/ScheduledTasksApi";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { TableColumn } from "components/pages/measurement-devices/utils/types";
import { ColumnBodyOptions } from "primereact/column";
import { useParams } from "react-router-dom";
import { T } from "services/lang/LanguageService";
import { useDisplayTimezone } from "hooks/useTimezone";
import { fmtDatetime } from "utils/time-utils";
import { DATE_PATTERN_SECONDS } from "utils/constants";
import { DataTableProps, DataTableStateEvent } from "primereact/datatable";
import UserAvatar from "../../user-avatar/UserAvatar";
import JSONObjectComparer from "../../json-object-comparer/JSONObjectComparer";

type ScheduledTaskHistoryTableProps = {
  currentConfig: string;
  onChange?: (taskHistory: IScheduledTaskHistoryDTO) => void;
};

const ROWS = 5;

function ScheduledTaskHistoryTable({ currentConfig, onChange }: ScheduledTaskHistoryTableProps) {
  const displayTz = useDisplayTimezone();
  const [pagination, setPagination] = useState({ first: 0, rows: ROWS });
  const { dagId, taskId } = useParams();

  const [diffHeader, setDiffHeader] = useState("");
  const [newConfig, setNewConfig] = useState("");
  const [oldConfig, setOldConfig] = useState("");

  const [configDialog, setConfigDialog] = useState(false);
  const [diffDialog, setDiffDialog] = useState(false);

  const [selectedConfig, setSelectedConfig] = useState<IScheduledTaskHistoryDTO | null>(null);
  const [tabIndex, setTabIndex] = useState(0);

  const { data, isLoading, isFetching, error, isSuccess } = useGetAirflowTaskHistories(
    {
      dagId: dagId!,
      taskId: taskId!,
      take: pagination.rows + 1, // added 1 to get additional history record for diff
      skip: pagination.first,
    },
    {
      keepPreviousData: true,
      enabled: !!dagId && !!taskId,
    }
  );

  useEffect(() => {
    if (selectedConfig) {
      onChange?.(selectedConfig);
    }
  }, [selectedConfig]);

  const configBody = (dataRow: IScheduledTaskHistoryDTO, { rowIndex }: ColumnBodyOptions) => {
    const setConfig = (dataRow: IScheduledTaskHistoryDTO) => {
      setDiffHeader(dataRow.changeOn);
      setNewConfig(dataRow.config);
      setOldConfig(data?.items ? data.items[rowIndex + 1]?.config : "");
    };

    const setCompareToCurrentCode = (dataRow: IScheduledTaskHistoryDTO) => {
      setDiffHeader(T("label.compare-to-current-code"));
      setNewConfig(currentConfig);
      setOldConfig(dataRow.config);
    };

    return (
      <div className="dialog-button-wrapper">
        <Button
          icon="pi pi-cog"
          tooltip={T("word.config")}
          className="p-button-sm"
          onClick={() => {
            setConfigDialog(true);
            setConfig(dataRow);
          }}
        />
        <Button
          icon="pi pi-clock"
          tooltip={T("label.compare-to-current-config")}
          className="p-button-sm"
          onClick={() => {
            setDiffDialog(true);
            setCompareToCurrentCode(dataRow);
          }}
        />
        <Button
          icon="pi pi-history"
          className="p-button-sm"
          tooltip={T("label.compare-to-previous-config")}
          onClick={() => {
            setDiffDialog(true);
            setConfig(dataRow);
          }}
        />
      </div>
    );
  };

  const changedByBody = (rowData: any) => {
    return <UserAvatar username={rowData.changeBy} />;
  };

  let header = "";
  if (diffHeader) {
    if (diffHeader !== T("label.compare-to-current-code")) {
      header = fmtDatetime(diffHeader, displayTz, DATE_PATTERN_SECONDS);
    } else {
      header = diffHeader;
    }
  }
  const decodeBase64Config = (base64: string) => {
    try {
      if (!base64) return T("message.there-is-no-data-to-decode");
      const decodedString = atob(base64);
      if (decodedString.charCodeAt(0) === 0x50 && decodedString.charCodeAt(1) === 0x4b) {
        return T("message.zip-file-detected");
      }

      return decodedString;
    } catch (error) {
      easasAlert({
        summary: T("error.failed-to-decode-data"),
        severity: "error",
      });
      return T("error.invalid-base64-data");
    }
  };

  const onPage = (e: DataTableStateEvent) => {
    setPagination({ first: e.first, rows: e.rows });
  };

  const taskHistoryColumns: TableColumn<IScheduledTaskHistoryDTO>[] = [
    {
      header: "",
      selectionMode: "single",
      columnKey: "select",
    },
    {
      field: "changeOn",
      columnKey: "changeOn",
      header: T("word.date"),
      body: data => fmtDatetime(data.changeOn, displayTz, DATE_PATTERN_SECONDS),
    },
    {
      field: "changeBy",
      columnKey: "changeBy",
      header: T("label.changed-by"),
      body: changedByBody,
    },
    {
      columnKey: "actions",
      header: "",
      body: configBody,
    },
  ];

  const tableConfig: DataTableProps<IScheduledTaskHistoryDTO[]> = {
    loading: isLoading || isFetching,
    lazy: true,
    rows: pagination.rows,
    first: pagination.first,
    onPage,
    totalRecords: data?.pageInfo.total ?? 0,
    onSelectionChange: e => setSelectedConfig(e.value),
    selectionMode: "single",
    selection: selectedConfig,
    dataKey: "changeOn",
  };

  return (
    <>
      {error && <span>{T("error.task-history-template-selection-error")}</span>}

      {isLoading && !error && <span>{T("message.task-history-templates-is-loading")}</span>}

      {isSuccess && (
        <DataTable columns={taskHistoryColumns} data={data?.items ?? []} tableConfig={tableConfig} />
      )}

      <Dialog
        header={T("word.config")}
        onHide={() => setConfigDialog(false)}
        visible={configDialog}
        blockScroll
      >
        {newConfig && (
          <TabView activeIndex={tabIndex} onTabChange={e => setTabIndex(e.index)}>
            <TabPanel header={T("word.config")}>
              <Editor
                height="100vh"
                width="60vw"
                defaultLanguage="json"
                value={JSON.stringify(new ScheduledTaskDTO(JSON.parse(newConfig)), null, 4)}
                options={{ readOnly: true }}
              />
            </TabPanel>
            <TabPanel header={T("action.read-code")}>
              <Editor
                height="100vh"
                width="60vw"
                defaultLanguage="json"
                value={decodeBase64Config(JSON.parse(newConfig).codeB64)}
                options={{ readOnly: true }}
              />
            </TabPanel>
          </TabView>
        )}
      </Dialog>

      <Dialog
        onHide={() => setDiffDialog(false)}
        visible={diffDialog}
        blockScroll
        header={header}
        style={{ width: "min(100vw, 1000px)", height: "90vh" }}
      >
        {newConfig && oldConfig && (
          <JSONObjectComparer
            oldValue={JSON.stringify(new ScheduledTaskDTO(JSON.parse(oldConfig)), null, 4)}
            newValue={JSON.stringify(new ScheduledTaskDTO(JSON.parse(newConfig)), null, 4)}
            rightTitle={
              diffHeader === T("label.compare-to-current-code") ? T("label.current-code") : undefined
            }
          />
        )}
      </Dialog>
    </>
  );
}

export default ScheduledTaskHistoryTable;
